/** Announcer-Config pro Server: enabled, interval (>=10s), rotierende Messages. */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, activeServer } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { t } from "../i18n";

interface Cfg { enabled: boolean; interval: number; messages: string[] }

export function Announcer() {
  const enabled = useSignal(false);
  const interval = useSignal(120);
  const messages = useSignal<string[]>([]);
  const loading = useSignal(false);
  const edit = useSignal(false);

  async function load() {
    const sv = activeServer.value;
    if (!sv) return;
    loading.value = true;
    try {
      const r = await api.call<ApiResponse & { config: Cfg }>("announcer_get", { server: sv });
      if (r.ok) { enabled.value = r.config.enabled; interval.value = r.config.interval; messages.value = r.config.messages.length ? r.config.messages : [""]; }
    } finally {
      loading.value = false;
    }
  }
  useSignalEffect(() => { if (activeServer.value) void load(); });

  async function save() {
    const sv = activeServer.value;
    if (!sv) return;
    const msgs = messages.value.map((m) => m.trim()).filter((m) => m !== "");
    const r = await api.call<ApiResponse & { config: Cfg }>("announcer_save", { server: sv, enabled: enabled.value, interval: interval.value, messages: msgs });
    if (r.ok) { toast(t("announcer.saved"), "ok"); interval.value = r.config.interval; }
    else toast(String(r.error ?? t("common.error")), "err");
  }

  const setMsg = (i: number, v: string) => { const a = [...messages.value]; a[i] = v; messages.value = a; };
  const delMsg = (i: number) => { messages.value = messages.value.filter((_, j) => j !== i); };

  if (!activeServer.value) return (<><PageHead title={t("nav.announcer")} /><NoServer /></>);

  const items: PanelItem[] = [
    { id: "messages", title: t("announcer.messages"), defaultSpan: 8, render: () => (
      <Panel title={t("announcer.messages")} actions={<button class="btn sm" onClick={() => (messages.value = [...messages.value, ""])}>{t("announcer.addMessage")}</button>}>
        <div class="stack">
          {messages.value.length === 0 && <div class="faint">{t("announcer.empty")}</div>}
          {messages.value.map((m, i) => (
            <div key={i} class="row">
              <span class="mono faint" style="width:22px;text-align:right">{i + 1}</span>
              <input class="input grow" value={m} onInput={(e) => setMsg(i, (e.target as HTMLInputElement).value)} />
              <button class="btn sm ghost" onClick={() => delMsg(i)} aria-label={t("common.remove")}>✕</button>
            </div>
          ))}
          <div class="caption">{t("announcer.maxHint")}</div>
        </div>
      </Panel>
    ) },
    { id: "settings", title: t("nav.settings"), defaultSpan: 4, render: () => (
      <Panel title={t("nav.settings")}>
        <div class="stack">
          <label class="row" style="justify-content:space-between">
            <span>{t("announcer.enabled")}</span>
            <input type="checkbox" checked={enabled.value} onChange={(e) => (enabled.value = (e.target as HTMLInputElement).checked)} />
          </label>
          <div class="field">
            <label>{t("announcer.intervalLabel")}</label>
            <input class="input mono" type="number" min={10} value={interval.value} onInput={(e) => (interval.value = Number((e.target as HTMLInputElement).value) || 120)} />
          </div>
        </div>
      </Panel>
    ) },
  ];

  return (
    <div class="view-enter">
      <PageHead title={t("nav.announcer")} sub={t("announcer.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        <button class="btn primary" onClick={() => void save()}>{t("common.save")}</button>
      </PageHead>
      <PanelGrid view="announcer" items={items} edit={edit} />
    </div>
  );
}
